/**
 * @function smoothedLinearRegression
 * @description Projects a future shoreline distance using the spatially smoothed Linear
 * Regression Rate (LRR). Only erosion (rate < 0) is projected forward; progradation (rate > 0)
 * is zeroed out, keeping the point at the shore's current position.
 * @param {Object} properties - Transect feature properties, from GEE.
 * @param {Number} horizon - Projection horizon, in years.
 * **Called by:
 * @see {projectDistance}
 * @memberof module:ShoreR
 * @returns {Number|null} Projected distance offset in meters, or null if `smoothed_lrr` is missing.
 */
const smoothedLinearRegression = (properties, horizon) => {
  const smoothedLrr = properties.smoothed_lrr;
  if (smoothedLrr == null) return null;

  const effectiveRate = Math.min(smoothedLrr, 0);
  return effectiveRate * horizon;
};

/**
 * @function smoothedEndPointRate
 * @description Projects a future shoreline distance using the spatially smoothed End Point Rate
 * (EPR). Only erosion (rate < 0) is projected forward; progradation (rate > 0) is zeroed out,
 * keeping the point at the shore's current position.
 * @param {Object} properties - Transect feature properties, from GEE.
 * @param {Number} horizon - Projection horizon, in years.
 * **Called by:
 * @see {projectDistance}
 * @memberof module:ShoreR
 * @returns {Number|null} Projected distance offset in meters, or null if `smoothed_epr` is missing.
 */
const smoothedEndPointRate = (properties, horizon) => {
  const smoothedEpr = properties.smoothed_epr;
  if (smoothedEpr == null) return null;

  const effectiveRate = Math.min(smoothedEpr, 0);
  return effectiveRate * horizon;
};

/**
 * @description Registry of available projection methods, keyed by method name.
 * @see {@link smoothedLinearRegression}
 * @see {@link smoothedEndPointRate}
 * @memberof module:ShoreR
 */
const METHODS = {
  smoothed_linear_regression: smoothedLinearRegression,
  smoothed_end_point_rate: smoothedEndPointRate,
};

/**
 * @function projectDistance
 * @description Projects a transect's future shoreline distance offset using the named method.
 * @param {String} method - Method key (e.g. "smoothed_linear_regression").
 * @param {Object} properties - Transect feature properties, from GEE.
 * @param {Number} horizon - Projection horizon, in years.
 * @see {@link METHODS}
 * **Called by:
 * @see {computeProjections}
 * @memberof module:ShoreR
 * @returns {Number|null} Projected distance offset in meters, or null if the method is unknown or fails.
 */
export const projectDistance = (method, properties, horizon) => {
  const fn = METHODS[method];
  if (!fn) {
    console.warn(`Unknown projection method: "${method}"`);
    return null;
  }
  return fn(properties, horizon);
};
